import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API_KEY_CONST } from '@/constant';
import {
  useAPIKeyCountQuery,
  useAPIKeyListQuery,
  useAPIKeyRemoveMutation,
  useAPIKeyUpdateMutation,
} from '@/hook/apiKey';
import {
  KeyVertical,
  VFlex,
  VLoadingSpinner,
  VStack,
  VStatusBadge,
  VSwitch,
  VTable,
  useVModal,
} from '@/package';
import { useAuthStore } from '@/store';
import type { APIKeyApi, APIKeyWithPermission } from '@/type';
import { ERROR_CODE } from '@/util';

import { APIKeyCreationButton } from './APIKeyCreationButton';
import { APIKeyDetailModal } from './APIKeyDetailModal';
import { makeAPIKeyDataForTable } from './uitl';

export const APIKeyList = () => {
  const { t } = useTranslation();
  const user = useAuthStore(state => state.user);
  const { data: apiKeyList, isLoading } = useAPIKeyListQuery();
  const { data: apiKeyCount } = useAPIKeyCountQuery({});
  const { data: activatedCount } = useAPIKeyCountQuery({
    status: API_KEY_CONST.API_KEY_STATUS.ENABLE,
  });
  const { mutateAsync: update } = useAPIKeyUpdateMutation();
  const { mutateAsync: remove } = useAPIKeyRemoveMutation();
  const { openDangerAlert, openDangerConfirm } = useVModal();
  const [selectedAPIKey, setSelectedAPIKey] =
    useState<Nullish<APIKeyWithPermission>>(null);
  const apiKeyTableData = useMemo(() => {
    if (isLoading || apiKeyList == null) return [];

    return apiKeyList.map(item => {
      const isEditablePermission =
        useAuthStore.getState().isAdmin() ||
        (useAuthStore.getState().isManager() && user.id === item.user.id);

      return makeAPIKeyDataForTable(item, isEditablePermission);
    });
  }, [apiKeyList, isLoading, user]);

  const handleUpdate = ({ id, name, status }: APIKeyApi.Update.Request) => {
    const mutatedResult = update({
      id,
      name,
      status,
    });

    mutatedResult.catch(err => {
      const { resultCode } = err.data;
      let message: string = t('error.defaultError.coperiodic');

      switch (resultCode as string) {
        case ERROR_CODE[45423]:
          message = t('apiKey.createError45423Message.rebunker', {
            limit: JSON.parse(err.data.resultMsg)?.limit,
          });
          break;
      }

      openDangerAlert({
        message,
        size: 'small',
        title: t('common.errorTitle.preconfirmation'),
      });
    });
  };

  return (
    <VStack className="h-full py-4" gap={0.25}>
      {isLoading ? (
        <VLoadingSpinner />
      ) : (
        <>
          <VFlex
            className="px-2 text-body-xl text-gray-500"
            gap={0.5}
            items="center"
          >
            <div>
              <span>Total&nbsp;</span>
              <span>{apiKeyCount ?? 0}</span>
            </div>
            {activatedCount && (
              <>
                <div className="h-4 w-px bg-gray-300"></div>
                <div className="font-semibold text-gray-500">
                  {t('apiKey.activateCount.seborrhea', {
                    count: activatedCount,
                  })}
                </div>
              </>
            )}
          </VFlex>

          <VTable
            className="h-full overflow-hidden"
            columnList={[
              'keyName',
              'status',
              'usage',
              'secretKey',
              'lastAccessed',
              'createdBy',
              'expirationTime',
              'activate',
            ]}
            columnStyleMap={{
              status: { className: 'w-[86px]' },
              usage: { className: 'w-[148px]' },
              secretKey: { className: 'w-[164px]' },
              lastAccessed: { className: 'w-[204px]' },
              createdBy: { className: 'w-[124px]' },
              expirationTime: { className: 'w-[204px]' },
              activate: { className: 'w-[68px]' },
            }}
            cellRenderer={{
              status: value => (
                <VStatusBadge
                  size="sm"
                  variant={
                    value === API_KEY_CONST.API_KEY_STATUS.DISABLE
                      ? 'notStarted'
                      : value === API_KEY_CONST.API_KEY_STATUS.ENABLE
                        ? 'inProgress'
                        : 'ready'
                  }
                >
                  {value}
                </VStatusBadge>
              ),
              usage: value => <div className="text-right">{value}</div>,
              activate: (value, _, { id, keyName, raw }) =>
                value != null ? (
                  <VFlex items="center" onClick={e => e.stopPropagation()}>
                    <VSwitch
                      checked={value}
                      disabled={!raw.isEditablePermission}
                      onChange={value => {
                        handleUpdate({
                          id,
                          name: keyName,
                          status:
                            value === false
                              ? API_KEY_CONST.API_KEY_STATUS.DISABLE
                              : API_KEY_CONST.API_KEY_STATUS.ENABLE,
                        });
                      }}
                    />
                  </VFlex>
                ) : (
                  ''
                ),
            }}
            data={apiKeyTableData}
            renderEmptyData={() => (
              <VStack className="w-full pt-40" gap={0.5} items="center">
                <VFlex>
                  <KeyVertical className="size-16.5 text-blue-300" />
                </VFlex>

                <VStack gap={1} items="center">
                  <div className="text-center leading-[24px] whitespace-pre-line text-blue-300">
                    {t('apiKey.listEmptyMessage.curvilinear')}
                  </div>

                  <APIKeyCreationButton />
                </VStack>
              </VStack>
            )}
            resizable
            setRowRemovable={row => row.status != null}
            setRowSelected={row =>
              row.status !== API_KEY_CONST.API_KEY_STATUS.INACTIVE
            }
            onRowClick={e => {
              const apiKey = e.target.row.raw as APIKeyWithPermission;

              setSelectedAPIKey(apiKey);
            }}
            onRemove={id => {
              openDangerConfirm({
                message: t('project.removeWarnTitle.falconidae'),
                title: t('project.removeAction.kalashnikov'),
                size: 'small',
                onConfirm() {
                  remove(id);
                },
              });
            }}
          />

          {selectedAPIKey && (
            <APIKeyDetailModal
              {...selectedAPIKey}
              onHide={() => setSelectedAPIKey(null)}
            />
          )}
        </>
      )}
    </VStack>
  );
};

APIKeyList.displayName = 'APIKeyList';
